/**
 * Provides an administrative interface for configuring the Oversight
 * dashboard widget and its reporting integration settings. Allows
 * administrators to manage the Power BI report URL used to display
 * oversight metrics, governance insights, and monitoring information
 * within the application.
 *
 * The component supports loading existing widget configurations,
 * updating analytics integration endpoints, tracking unsaved changes,
 * restoring previously saved settings, and persisting configuration
 * updates to ensure the Oversight widget is connected to the correct
 * reporting source.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const FEATURE_CODE = "dashboard.oversight";

const OversightConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [oversightPowerBiUrl, setOversightPowerBiUrl] = useState("");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const oversightConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(oversightConfig);

    setOversightPowerBiUrl(
      oversightConfig?.PowerBIOversightUrl || "",
    );
  };

  const hasChanges =
    savedConfig &&
    oversightPowerBiUrl !==
      (savedConfig.PowerBIOversightUrl || "");

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          PowerBIOversightUrl: oversightPowerBiUrl,
        },
      };

      await updateWidgetConfigurations(payload);

      const updatedConfig = payload.configs;

      setSavedConfig(updatedConfig);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setOversightPowerBiUrl(
      savedConfig.PowerBIOversightUrl || "",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Oversight Power BI URL
        </span>

        <input
          type="text"
          value={oversightPowerBiUrl}
          onChange={(e) =>
            setOversightPowerBiUrl(e.target.value)
          }
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Power BI URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default OversightConfig;